import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Botao from './Botao';

/**
 * ListaSuspensa — menu que abre ao lado de um botão. Fecha com Esc, com clique fora
 * e ao escolher um item; o foco volta para o botão que abriu.
 * itens: [{ rotulo, icone?, aoEscolher }]. O botão recebe `children` como rótulo.
 */
export default function ListaSuspensa({ itens, children, variante = 'secundario' }) {
  const [aberta, setAberta] = useState(false);
  const id = useId();
  const raiz = useRef(null);
  const gatilho = useRef(null);

  const fechar = useCallback(() => {
    setAberta(false);
    gatilho.current?.focus();
  }, []);

  useEffect(() => {
    if (!aberta) return undefined;
    const aoClicarFora = (e) => { if (!raiz.current?.contains(e.target)) setAberta(false); };
    const aoApertarEsc = (e) => { if (e.key === 'Escape') fechar(); };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoApertarEsc);
    raiz.current?.querySelector('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoApertarEsc);
    };
  }, [aberta, fechar]);

  const escolher = (item) => {
    setAberta(false);
    item.aoEscolher?.();
    gatilho.current?.focus();
  };

  return (
    <div ref={raiz} className="sj-lista-suspensa">
      <Botao
        ref={gatilho}
        variante={variante}
        aria-haspopup="menu"
        aria-expanded={aberta}
        aria-controls={id}
        onClick={() => setAberta((v) => !v)}
      >
        {children}
      </Botao>
      {aberta && (
        <div id={id} role="menu" className="sj-lista">
          {itens.map((item) => (
            <Botao key={item.rotulo} item role="menuitem" onClick={() => escolher(item)}>
              {item.icone && <span className="sj-icone" aria-hidden="true">{item.icone}</span>}
              {item.rotulo}
            </Botao>
          ))}
        </div>
      )}
    </div>
  );
}
